import React from "react";
import {makeStyles} from "@material-ui/core/styles";
import {Accordion, AccordionDetails, Button, Typography} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import {useTranslation} from "react-i18next";
import Flex from "components/grid/Flex";
import * as colors from "components/colors/Colors";
import ClinicalReportFindingCard from "./ClinicalReportFindingCard";
import LetterTextEditor from "./LetterTextEditor";
import {
    AI_CONTENT_LOCK_HANDLERS,
    AiHintRow,
    AiHintRowFromSchema,
    asArray,
    asText,
    ClinicalReportSummaryChip,
    comparisonLetterTextKey,
    DIAGNOSIS_CERTAINTY_CHIP_COLORS,
    EVIDENCE_SOURCE_STATUS_CHIP_COLORS,
    findingLetterTextKey,
    INFO_CHIP_COLORS,
    LockedAiHintsBox,
    OVERALL_SUMMARY_LETTER_TEXT_KEY,
    SlimAccordionSummary,
    sortHintsByPriority,
} from "./ClinicalReportSummaryParts";

const useStyles = makeStyles((theme) => ({
    root: {
        flexDirection: "column",
        gap: theme.spacing(2.5),
        maxWidth: 1000,
        width: "100%",
        margin: "0 auto",
    },
    header: {
        justifyContent: "space-between",
        alignItems: "flex-start",
        flexWrap: "wrap",
        gap: theme.spacing(2),
    },
    headerTitle: {
        fontWeight: 700,
    },
    headerMeta: {
        gap: theme.spacing(2),
        fontSize: "0.875rem",
        color: colors.clinicalMutedText,
    },
    copyAllButton: {
        background: colors.clinicalAccentTeal,
        color: colors.htmlWhite,
        textTransform: "none",
        "&:hover": {background: colors.clinicalAccentTealHover},
    },
    aiBanner: {
        display: "grid",
        gridTemplateColumns: "minmax(180px, 1fr) 2fr",
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        background: colors.clinicalNeutralBackground,
        border: `1px solid ${colors.clinicalOutline}`,
        borderRadius: 10,
        fontSize: "0.875rem",
    },
    aiBannerNotice: {
        fontWeight: 700,
    },
    aiBannerExplanation: {
        color: colors.clinicalMutedText,
    },
    mutedText: {
        color: colors.clinicalMutedText,
        fontSize: "0.8rem",
    },
    card: {
        flexDirection: "column",
        background: colors.htmlWhite,
        border: `1px solid ${colors.clinicalOutline}`,
        borderRadius: 10,
        overflow: "hidden",
    },
    overallSummaryCard: {
        borderColor: colors.clinicalAccentTeal,
        boxShadow: `0 0 0 1px ${colors.clinicalAccentTeal} inset`,
    },
    cardHead: {
        flexDirection: "column",
        padding: theme.spacing(2),
    },
    cardTitle: {
        fontWeight: 700,
    },
    hintList: {
        margin: 0,
        paddingLeft: theme.spacing(2.5),
    },
    sourcesAccordion: {
        boxShadow: "none",
        "&:before": {display: "none"},
        "&.Mui-expanded": {margin: 0},
    },
    sourcesContent: {
        flexDirection: "column",
        gap: theme.spacing(1),
        width: "100%",
        userSelect: "none",
        fontSize: "0.875rem",
    },
    sourcesHeading: {
        fontSize: "0.75rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: colors.clinicalMutedText,
    },
}));

const ClinicalReportSummaryResult = ({clinicalReportSummary, editedLetterTexts, onLetterTextChange, onLetterTextReset, onLetterTextCopy, isLetterTextCopyConfirmed}) => {
    const classes = useStyles();
    const {t: translate} = useTranslation();

    const findings = asArray(clinicalReportSummary.findings).filter((finding) => finding && typeof finding === "object");
    const overallSummary = clinicalReportSummary.overallSummary && typeof clinicalReportSummary.overallSummary === "object" ? clinicalReportSummary.overallSummary : {};
    const summaryQuality = clinicalReportSummary.quality && typeof clinicalReportSummary.quality === "object" ? clinicalReportSummary.quality : {};
    const summaryLimitations = [
        ...asArray(summaryQuality.unreadableFileNames).map(asText).filter(Boolean)
            .map((unreadableFileName) => translate("clinicalReportSummarizer.sources.unreadableFile", {fileName: unreadableFileName})),
        ...asArray(summaryQuality.unanalysableFindingIds).map(asText).filter(Boolean)
            .map((unanalysableFindingId) => translate("clinicalReportSummarizer.sources.unanalysableFinding", {findingId: unanalysableFindingId})),
        ...(summaryQuality.overallSummaryMissing === true ? [translate("clinicalReportSummarizer.sources.overallSummaryMissing")] : []),
        ...asArray(summaryQuality.limitations).map(asText).filter(Boolean),
    ];
    const evidenceSources = asArray(clinicalReportSummary.evidenceSources).filter((evidenceSource) => evidenceSource && typeof evidenceSource === "object");
    const diagnosisSuggestions = asArray(overallSummary.diagnosisSuggestions).filter((diagnosisSuggestion) => diagnosisSuggestion && typeof diagnosisSuggestion === "object");
    const recommendedMeasures = asArray(overallSummary.recommendedMeasures).map(asText).filter(Boolean);
    const crossFindingHints = sortHintsByPriority(overallSummary.crossFindingHints);

    const letterTextOf = (letterTextKey, aiLetterText) =>
        (typeof editedLetterTexts[letterTextKey] === "string" ? editedLetterTexts[letterTextKey] : asText(aiLetterText)).trim();

    const copyAllLetterTexts = () => {
        const findingLetterTexts = findings.map((finding, findingIndex) => {
            const letterTextKey = findingLetterTextKey(finding, findingIndex);
            const findingLetterParagraphs = [
                letterTextOf(letterTextKey, finding.letterText),
                letterTextOf(comparisonLetterTextKey(letterTextKey), finding.comparison?.letterTextAddition),
            ].filter(Boolean);
            return `${asText(finding.title) || asText(finding.findingType)}:\n${findingLetterParagraphs.join("\n")}`;
        });
        const overallSummaryLetterText = letterTextOf(OVERALL_SUMMARY_LETTER_TEXT_KEY, overallSummary.letterSummary);
        const allLetterTexts = overallSummaryLetterText
            ? [...findingLetterTexts, `${translate("clinicalReportSummarizer.overallSummary.title")}:\n${overallSummaryLetterText}`]
            : findingLetterTexts;
        onLetterTextCopy(allLetterTexts.join("\n\n"));
    };

    return (
        <Flex container className={classes.root}>
            <Flex container className={classes.header}>
                <Flex container direction={"column"}>
                    <Typography variant={"h5"} className={classes.headerTitle}>
                        {translate("clinicalReportSummarizer.result.title")}
                    </Typography>
                    <Flex container className={classes.headerMeta}>
                        <span>
                            {translate("clinicalReportSummarizer.result.mode")}{" "}
                            <b>{translate(`clinicalReportSummarizer.summaryMode.${asText(clinicalReportSummary.parameters?.mode)}.title`, {defaultValue: asText(clinicalReportSummary.parameters?.mode)})}</b>
                        </span>
                        <span>{translate("clinicalReportSummarizer.result.findingCount", {count: findings.length})}</span>
                    </Flex>
                </Flex>
                <Button variant={"contained"} className={classes.copyAllButton} startIcon={<FileCopyIcon />} onClick={copyAllLetterTexts}>
                    {translate("clinicalReportSummarizer.result.copyAll")}
                </Button>
            </Flex>

            <div className={classes.aiBanner}>
                <span className={classes.aiBannerNotice}>
                    {translate("clinicalReportSummarizer.result.aiDraftNotice")}
                </span>
                <span className={classes.aiBannerExplanation}>{translate("clinicalReportSummarizer.result.aiDraftExplanation")}</span>
            </div>

            <Flex container className={`${classes.card} ${classes.overallSummaryCard}`}>
                <Flex container className={classes.cardHead}>
                    <Typography variant={"h6"} className={classes.cardTitle}>{translate("clinicalReportSummarizer.overallSummary.title")}</Typography>
                    <Typography className={classes.mutedText}>{translate("clinicalReportSummarizer.overallSummary.subtitle")}</Typography>
                </Flex>
                <LetterTextEditor
                    label={translate("clinicalReportSummarizer.letterText.label")}
                    aiLetterText={overallSummary.letterSummary}
                    editedLetterText={editedLetterTexts[OVERALL_SUMMARY_LETTER_TEXT_KEY]}
                    onLetterTextChange={(letterText) => onLetterTextChange(OVERALL_SUMMARY_LETTER_TEXT_KEY, letterText)}
                    onLetterTextReset={() => onLetterTextReset(OVERALL_SUMMARY_LETTER_TEXT_KEY)}
                    onLetterTextCopy={onLetterTextCopy}
                    isLetterTextCopyConfirmed={isLetterTextCopyConfirmed}
                />
                <LockedAiHintsBox hasHints={diagnosisSuggestions.length > 0 || recommendedMeasures.length > 0 || crossFindingHints.length > 0}>
                    {diagnosisSuggestions.length > 0 && (
                        <AiHintRow
                            chip={<ClinicalReportSummaryChip label={translate("clinicalReportSummarizer.overallSummary.suggestion")} chipColors={INFO_CHIP_COLORS} />}
                            title={translate("clinicalReportSummarizer.overallSummary.diagnosisSuggestions")}
                            recommendation={translate("clinicalReportSummarizer.overallSummary.diagnosisSuggestionsCheck")}
                        >
                            <ul className={classes.hintList}>
                                {diagnosisSuggestions.map((diagnosisSuggestion, diagnosisSuggestionIndex) => (
                                    <li key={diagnosisSuggestionIndex}>
                                        {asText(diagnosisSuggestion.text)}
                                        {asText(diagnosisSuggestion.icd10GmCode) && ` (${asText(diagnosisSuggestion.icd10GmCode)})`}{" "}
                                        <ClinicalReportSummaryChip
                                            labelPrefix={"clinicalReportSummarizer.diagnosisCertainty"}
                                            schemaValue={diagnosisSuggestion.certainty}
                                            chipColorsBySchemaValue={DIAGNOSIS_CERTAINTY_CHIP_COLORS}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </AiHintRow>
                    )}
                    {recommendedMeasures.length > 0 && (
                        <AiHintRow
                            chip={<ClinicalReportSummaryChip label={translate("clinicalReportSummarizer.overallSummary.measures")} chipColors={INFO_CHIP_COLORS} />}
                            title={translate("clinicalReportSummarizer.overallSummary.recommendedMeasures")}
                        >
                            <ul className={classes.hintList}>
                                {recommendedMeasures.map((recommendedMeasure, recommendedMeasureIndex) => (
                                    <li key={recommendedMeasureIndex}>{recommendedMeasure}</li>
                                ))}
                            </ul>
                        </AiHintRow>
                    )}
                    {crossFindingHints.map((crossFindingHint, crossFindingHintIndex) => (
                        <AiHintRowFromSchema key={crossFindingHintIndex} aiHint={crossFindingHint} />
                    ))}
                </LockedAiHintsBox>
            </Flex>

            {findings.map((finding, findingIndex) => (
                <ClinicalReportFindingCard
                    key={findingLetterTextKey(finding, findingIndex)}
                    finding={finding}
                    findingIndex={findingIndex}
                    editedLetterTexts={editedLetterTexts}
                    onLetterTextChange={onLetterTextChange}
                    onLetterTextReset={onLetterTextReset}
                    onLetterTextCopy={onLetterTextCopy}
                    isLetterTextCopyConfirmed={isLetterTextCopyConfirmed}
                />
            ))}

            {(summaryLimitations.length > 0 || evidenceSources.length > 0) && (
                <Flex container className={classes.card}>
                    <Accordion className={classes.sourcesAccordion} TransitionProps={{unmountOnExit: true}}>
                        <SlimAccordionSummary title={translate("clinicalReportSummarizer.sources.title")} />
                        <AccordionDetails>
                            <Flex container className={classes.sourcesContent} {...AI_CONTENT_LOCK_HANDLERS}>
                                {summaryLimitations.length > 0 && (
                                    <>
                                        <Typography className={classes.sourcesHeading}>{translate("clinicalReportSummarizer.sources.limitations")}</Typography>
                                        <ul className={classes.hintList}>
                                            {summaryLimitations.map((summaryLimitation, summaryLimitationIndex) => (
                                                <li key={summaryLimitationIndex}>{summaryLimitation}</li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                                {evidenceSources.length > 0 && (
                                    <>
                                        <Typography className={classes.sourcesHeading}>{translate("clinicalReportSummarizer.sources.guidelines")}</Typography>
                                        <ul className={classes.hintList}>
                                            {evidenceSources.map((evidenceSource, evidenceSourceIndex) => (
                                                <li key={evidenceSourceIndex}>
                                                    <b>{asText(evidenceSource.id)}</b>{" "}
                                                    {[asText(evidenceSource.publisher), asText(evidenceSource.title), asText(evidenceSource.year)].filter(Boolean).join(", ")}{" "}
                                                    <ClinicalReportSummaryChip
                                                        labelPrefix={"clinicalReportSummarizer.evidenceSourceStatus"}
                                                        schemaValue={evidenceSource.status}
                                                        chipColorsBySchemaValue={EVIDENCE_SOURCE_STATUS_CHIP_COLORS}
                                                    />
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                            </Flex>
                        </AccordionDetails>
                    </Accordion>
                </Flex>
            )}
        </Flex>
    );
};

export default ClinicalReportSummaryResult;
